import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Home, Satellite, ListChecks } from 'lucide-react';
import { Button } from '../ui/button';
import { SERVICE_CATEGORIES } from './LeadCRM';

const NewEstimateChooser = () => {
  const navigate = useNavigate();

  // Preserve any query string (e.g., lead_id=... coming from a CRM lead card)
  const qs = typeof window !== 'undefined' ? window.location.search : '';

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="new-estimate-chooser">
      <div className="mb-8">
        <Link to="/dashboard/estimates">
          <Button variant="outline" size="sm" className="mb-4" data-testid="back-to-estimates-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Estimates
          </Button>
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Create New Estimate</h1>
        <p className="text-gray-600 mt-2">Choose the estimator that fits your job</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Generic Line-Item Estimator */}
        <button
          onClick={() => navigate(`/dashboard/estimates/new/line-item${qs}`)}
          className="group text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-blue-500 hover:shadow-xl transition-all p-8 flex flex-col"
          data-testid="choose-line-item-estimator"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-orange-500 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
            <ListChecks className="w-7 h-7 text-white" />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold bg-blue-100 text-blue-700 px-2 py-1 rounded-full">Any Trade</span>
            <span className="text-xs font-semibold bg-orange-100 text-orange-700 px-2 py-1 rounded-full">Recommended</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Quick Job Estimate
          </h2>
          <p className="text-gray-600 mb-5 flex-grow">
            Build a custom line-item estimate for any home service job. Materials, labor, tax, discount — all calculated automatically.
          </p>
          <div className="flex flex-wrap gap-1.5 mb-5">
            {SERVICE_CATEGORIES.slice(0, 8).map(cat => (
              <span key={cat.id} className={`text-xs px-2 py-0.5 rounded-full ${cat.color}`}>
                {cat.icon} {cat.label}
              </span>
            ))}
          </div>
          <div className="text-blue-600 font-semibold group-hover:translate-x-1 transition-transform">
            Start Line-Item Estimate →
          </div>
        </button>

        {/* Satellite Roofing Estimator */}
        <button
          onClick={() => navigate(`/dashboard/estimates/new/satellite${qs}`)}
          className="group text-left bg-white rounded-2xl border-2 border-gray-200 hover:border-orange-500 hover:shadow-xl transition-all p-8 flex flex-col"
          data-testid="choose-satellite-estimator"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
            <Satellite className="w-7 h-7 text-white" />
          </div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-1 rounded-full">🏘️ Roofing</span>
            <span className="text-xs font-semibold bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Specialized</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">
            Satellite Roof Estimate
          </h2>
          <p className="text-gray-600 mb-5 flex-grow">
            Generate a roof measurement and pricing estimate using satellite imagery — just enter the property address. Best for roof replacement and repair jobs.
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-5">
            <div className="flex items-start gap-2">
              <Home className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-orange-800">
                Includes auto-measurement, roof-type pricing, and Xactimate-ready output for insurance claims.
              </p>
            </div>
          </div>
          <div className="text-orange-600 font-semibold group-hover:translate-x-1 transition-transform">
            Start Satellite Estimate →
          </div>
        </button>
      </div>

      <div className="mt-8 bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 rounded-xl p-5 text-center">
        <p className="text-sm text-gray-700">
          💡 <span className="font-semibold">Not sure which to use?</span> Pick <span className="font-semibold text-blue-600">Quick Job Estimate</span> for anything that isn't a roof — kitchens, baths, HVAC, decks, windows, electrical, hardscaping, and more.
        </p>
      </div>
    </div>
  );
};

export default NewEstimateChooser;
